/**
 * Streaming Response Service for Vocalira - Low Latency Responses
 * 
 * Handles Server-Sent Events (SSE) for streaming chat responses
 * with parallel audio playback.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Audio Queue for sequential playback of streaming audio chunks
 */
class AudioQueue {
  constructor() {
    this.queue = [];
    this.isPlaying = false;
    this.currentAudio = null;
    this.onPlaybackStart = null;
    this.onPlaybackEnd = null;
    this.onChunkPlay = null;
    this.stopped = false;
  }

  /**
   * Add audio chunk to queue
   * @param {string} base64Audio - Base64 encoded audio
   * @param {string} text - Text being spoken
   * @param {number} index - Chunk index for ordering
   */
  enqueue(base64Audio, text, index) {
    this.queue.push({ audio: base64Audio, text, index });
    // Sort by index to ensure correct order
    this.queue.sort((a, b) => a.index - b.index);
    
    // Start playback if not already playing
    if (!this.isPlaying && !this.stopped) {
      this.playNext();
    }
  }

  /**
   * Play next audio in queue
   */
  async playNext() {
    if (this.stopped || this.queue.length === 0) {
      this.isPlaying = false;
      if (this.onPlaybackEnd) {
        this.onPlaybackEnd();
      }
      return;
    }

    this.isPlaying = true;
    const { audio, text, index } = this.queue.shift();

    try {
      // Notify chunk starting
      if (this.onChunkPlay) {
        this.onChunkPlay(text, index);
      }

      // Create audio element
      this.currentAudio = new Audio(`data:audio/mp3;base64,${audio}`);
      
      // First chunk starts playback notification
      if (index === 0 && this.onPlaybackStart) {
        this.onPlaybackStart();
      }

      // Wait for audio to finish
      await new Promise((resolve, reject) => {
        this.currentAudio.onended = resolve;
        this.currentAudio.onerror = reject;
        this.currentAudio.play().catch(reject);
      });

      // Play next
      this.playNext();
    } catch (error) {
      console.error('[AudioQueue] Playback error:', error);
      // Try next chunk anyway
      this.playNext();
    }
  }

  /**
   * Stop playback and clear queue
   */
  stop() {
    this.stopped = true;
    this.queue = [];
    
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    this.isPlaying = false;
  }

  /**
   * Reset for new stream
   */
  reset() {
    this.stop();
    this.stopped = false;
  }
}


/**
 * Stream chat message and handle responses
 * 
 * @param {Object} options - Streaming options
 * @param {string} options.message - User message to send
 * @param {string} options.conversationId - Conversation ID (optional)
 * @param {Function} options.onTextChunk - Called for each text chunk
 * @param {Function} options.onAudioReady - Called when audio chunk is ready
 * @param {Function} options.onComplete - Called when stream completes
 * @param {Function} options.onError - Called on error
 * @param {boolean} options.playAudio - Whether to play audio automatically
 * @returns {Object} Controller with stop() method
 */
export const streamChatMessage = async (options) => {
  const {
    message,
    conversationId = null,
    onTextChunk = () => {},
    onAudioReady = () => {},
    onComplete = () => {},
    onError = () => {},
    onPlaybackStart = () => {},
    onPlaybackEnd = () => {},
    playAudio = true,
  } = options;

  const token = localStorage.getItem('token');
  if (!token) {
    onError('Not authenticated');
    return { stop: () => {} };
  }

  // Create audio queue
  const audioQueue = new AudioQueue();
  audioQueue.onPlaybackStart = onPlaybackStart;
  audioQueue.onPlaybackEnd = onPlaybackEnd;

  // Accumulated text
  let fullText = '';
  let aborted = false;

  // Create AbortController for cancellation
  const abortController = new AbortController();

  try {
    const response = await fetch(`${API}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        is_voice_input: true,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status}`);
    }

    // Read SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!aborted) {
      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete events in buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(line.slice(6));

          switch (data.type) {
            case 'text':
              fullText += data.content;
              onTextChunk(data.content, fullText);
              break;

            case 'audio':
              if (playAudio) {
                audioQueue.enqueue(data.content, data.text, data.index);
              }
              onAudioReady(data.content, data.text, data.index);
              break;

            case 'done':
              // P0 FIX: Include translation from streaming response
              onComplete({
                fullText: data.full_text || fullText,
                conversationId: data.conversation_id,
                messageId: data.message_id,
                translation: data.translation || null,  // New: translation from backend
              });
              break;

            case 'error':
              onError(data.message);
              break;

            case 'user_saved':
              // User message saved, continue
              break;

            default:
              console.warn('[StreamChat] Unknown event type:', data.type);
          }
        } catch (e) {
          console.warn('[StreamChat] Failed to parse event:', line, e);
        }
      }
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('[StreamChat] Stream error:', error);
      onError(error.message);
    }
  }

  // Return controller
  return {
    stop: () => {
      aborted = true;
      abortController.abort();
      audioQueue.stop();
    },
    audioQueue,
  };
};


/**
 * Hook-friendly streaming chat function
 * Returns accumulated state
 */
export const useStreamingChat = () => {
  let currentStream = null;
  let audioQueue = null;

  const sendStreaming = async ({
    message,
    conversationId,
    onText,
    onAudio,
    onComplete,
    onError,
    onSpeakingStart,
    onSpeakingEnd,
  }) => {
    // Stop any existing stream
    if (currentStream) {
      currentStream.stop();
    }

    return new Promise((resolve, reject) => {
      streamChatMessage({
        message,
        conversationId,
        onTextChunk: (chunk, full) => {
          if (onText) onText(chunk, full);
        },
        onAudioReady: (audio, text, index) => {
          if (onAudio) onAudio(audio, text, index);
        },
        onComplete: (result) => {
          if (onComplete) onComplete(result);
          resolve(result);
        },
        onError: (err) => {
          if (onError) onError(err);
          reject(new Error(err));
        },
        onPlaybackStart: () => {
          if (onSpeakingStart) onSpeakingStart();
        },
        onPlaybackEnd: () => {
          if (onSpeakingEnd) onSpeakingEnd();
        },
        playAudio: true,
      }).then(stream => {
        currentStream = stream;
        audioQueue = stream.audioQueue;
      });
    });
  };

  const stop = () => {
    if (currentStream) {
      currentStream.stop();
      currentStream = null;
    }
  };

  const stopAudio = () => {
    if (audioQueue) {
      audioQueue.stop();
    }
  };

  return { sendStreaming, stop, stopAudio };
};


/**
 * Calculate estimated response time based on message complexity
 */
export const estimateResponseTime = (messageLength, userLevel) => {
  // Base latency (network + model startup)
  let baseMs = 300;
  
  // Add based on message length (more context = more processing)
  const contextMs = Math.min(messageLength * 2, 500);
  
  // User level affects prompt complexity
  const levelMultiplier = {
    beginner: 1.0,
    intermediate: 1.1,
    advanced: 1.2,
  };
  
  const multiplier = levelMultiplier[userLevel] || 1.0;
  
  return Math.round((baseMs + contextMs) * multiplier);
};


export default {
  streamChatMessage,
  useStreamingChat,
  estimateResponseTime,
  AudioQueue,
};
